<template>
  <article
    class="serviceFeatureBlock"
    :class="[
      `serviceFeatureBlock--${tone}`,
      { 'serviceFeatureBlock--reverse': reverse },
    ]"
  >
    <div class="serviceFeatureBlock__frame">
      <figure class="serviceFeatureBlock__media">
        <slot name="media" />
      </figure>
      <div class="serviceFeatureBlock__content">
        <h4 :id="titleId" class="serviceFeatureBlock__title">
          <slot name="title">{{ title }}</slot>
        </h4>
        <div class="serviceFeatureBlock__body">
          <p
            v-if="copyHtml"
            class="serviceFeatureBlock__copy"
            v-html="copyHtml"
          ></p>
          <p v-else class="serviceFeatureBlock__copy">
            <slot />
          </p>
        </div>
        <div v-if="$slots.actions" class="serviceFeatureBlock__actions">
          <slot name="actions" />
        </div>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: "ServiceFeatureBlock",
  props: {
    title: {
      type: String,
      default: "",
    },
    titleId: {
      type: String,
      default: "",
    },
    tone: {
      type: String,
      default: "secondary",
    },
    reverse: {
      type: Boolean,
      default: false,
    },
    copyHtml: {
      type: String,
      default: "",
    },
  },
};
</script>

<style lang="scss" scoped>
.serviceFeatureBlock {
  --service-feature-media-width: clamp(320px, 34vw, 430px);
  --service-feature-gap: clamp(22px, 2.8vw, 34px);
  --service-feature-tone: #{$color-secondary};
  --service-feature-tone-shadow: rgba(254, 185, 20, 0.2);
  --service-feature-surface: rgba(254, 185, 20, 0.14);
  --service-feature-surface-ring: rgba(254, 185, 20, 0.24);
  width: min(100%, 1080px);
  margin: 0 auto;
  position: relative;
  padding: clamp(18px, 2.4vw, 28px);
  border-radius: clamp(26px, 3vw, 34px);
  background: #ffffff;
  box-shadow: 0 22px 48px rgba(0, 42, 84, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.94);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: clamp(18px, 2.2vw, 28px);
    left: clamp(18px, 2.4vw, 28px);
    width: clamp(68px, 9vw, 102px);
    height: 6px;
    border-radius: 999px;
    background: var(--service-feature-tone);
    box-shadow: 0 10px 22px var(--service-feature-tone-shadow);
  }

  &__frame {
    display: flex;
    flex-direction: column;
    gap: clamp(20px, 2.6vw, 32px);
    padding-top: clamp(18px, 2.2vw, 26px);

    @media screen and (min-width: 992px) {
      display: grid;
      grid-template-columns: var(--service-feature-media-width) minmax(0, 1fr);
      align-items: stretch;
      column-gap: var(--service-feature-gap);
    }
  }

  &--reverse {
    @media screen and (min-width: 992px) {
      .serviceFeatureBlock__frame {
        grid-template-columns: minmax(0, 1fr) var(--service-feature-media-width);
      }

      .serviceFeatureBlock__media {
        order: 2;
      }

      .serviceFeatureBlock__content {
        order: 1;
      }
    }
  }

  &__media {
    width: 100%;
    margin: 0;
    padding: clamp(8px, 1vw, 12px);
    border-radius: clamp(24px, 3vw, 30px);
    background: #ffffff;
    box-shadow: 0 18px 38px rgba(0, 42, 84, 0.12),
      inset 0 0 0 1px rgba(0, 42, 84, 0.08);

    @media screen and (min-width: 992px) {
      height: 100%;
    }

    :deep(img) {
      display: block;
      width: 100%;
      aspect-ratio: 16 / 11;
      object-fit: cover;
      border-radius: clamp(18px, 2.2vw, 24px);
      box-shadow: rgba(0, 0, 0, 0.12) 0px 14px 28px;

      @media screen and (min-width: 992px) {
        height: 100%;
        min-height: clamp(240px, 24vw, 320px);
        aspect-ratio: auto;
      }
    }
  }

  &__content {
    min-width: 0;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    padding: clamp(22px, 3vw, 34px);
    border-radius: clamp(24px, 3vw, 30px);
    background: var(--service-feature-surface);
    box-shadow: inset 0 0 0 1px var(--service-feature-surface-ring),
      inset 0 16px 26px rgba(255, 255, 255, 0.34);

    &::before {
      content: "";
      display: block;
      width: 56px;
      height: 4px;
      border-radius: 999px;
      background: var(--service-feature-tone);
      box-shadow: 0 8px 18px var(--service-feature-tone-shadow);
    }
  }

  &__title {
    margin: 0;
    max-width: 100%;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: clamp(1.22rem, 1.04rem + 0.72vw, 1.82rem);
    line-height: 1.04;
    letter-spacing: 0.025em;
    text-align: left;
    color: $color-primary;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.82),
      0 10px 24px rgba(0, 42, 84, 0.08);
    text-wrap: balance;
  }

  &__body {
    min-width: 0;
  }

  &__actions {
    display: flex;
    justify-content: flex-start;
    margin-top: 2px;
  }

  &__copy {
    color: $color-text;
    width: 100%;
    max-width: none;
    margin: 0;
    font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
    font-size: clamp(1rem, 0.95rem + 0.3vw, 1.12rem);
    line-height: 1.76;
    letter-spacing: 0.01em;
    text-align: left;

    :deep(strong) {
      color: $color-primary;
      font-weight: 700;
    }
  }

  &--secondary {
    --service-feature-tone: #{$color-secondary};
    --service-feature-tone-shadow: rgba(254, 185, 20, 0.22);
    --service-feature-surface: rgba(254, 185, 20, 0.14);
    --service-feature-surface-ring: rgba(254, 185, 20, 0.24);
  }

  &--primary {
    --service-feature-tone: #{$color-primary};
    --service-feature-tone-shadow: rgba(0, 42, 84, 0.16);
    --service-feature-surface: rgba(0, 42, 84, 0.06);
    --service-feature-surface-ring: rgba(0, 42, 84, 0.14);
  }

  @media screen and (max-width: 767px) {
    padding: 18px;
    border-radius: 28px;

    &::before {
      top: 18px;
      left: 18px;
      height: 5px;
    }

    &__content {
      padding: 20px 18px;
      gap: 14px;
    }

    &__title {
      font-size: clamp(1.1rem, 1rem + 0.6vw, 1.42rem);
    }

    &__copy {
      font-size: 1rem;
      line-height: 1.7;
    }
  }
}
</style>
